import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { ClickAwayListener } from '@material-ui/core';
import { makeStyles } from 'design-system/product/theme';
import { addCodeFieldsToMarkdown, getHelperText } from './helperText';
import TextAutoComplete from './TextAutoComplete';
import { LogBehaviourActions } from '@scale/shared/types/params/textcollection';
import { ChatTaskBehaviorLogMetadata } from '@scale/shared/types/chat';
import { useAutoComplete } from './document';
import { SearchResult } from './ocr';
import validator from 'validator';
import _ from 'lodash';
import { TextArea, HelperText, Button, TextField, Typography } from '@scale/scaleui';
import {
  FileUploadProps,
  MultipartFileUpload,
} from 'react-scale/components/scalers/tasks/GenericDataCollection/Uploader';
import { MathEditor } from './MathEditor';
import { analytics } from 'react-scale/lib/analytics/segment-source-scalers';
import { useAttemptBehaviorLog } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/AttemptBehaviorLogContext';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import FieldHeader from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/FieldHeader';
import { twMerge } from 'tailwind-merge';
import { replaceWithDoubleDollarSign } from '../ThreadedInteraction/components/ChatMarkdown';
import UtilitiesWrapper from 'react-scale/components/scalers/tasks/common/UtilitiesWrapper';

type CustomTextFieldClass = 'root';

interface CustomTextFieldProps {
  isTestQuestion?: boolean;
  fieldId?: string;
  minChars?: number;
  maxChars?: number;
  minTokens?: number;
  maxTokens?: number;
  disabled?: boolean;
  index: number;
  value: string;
  setIndexValue: (index: number, value: string) => void;
  title?: string;
  description?: string;
  classes?: Record<CustomTextFieldClass, string>;
  autoFocus?: boolean;
  hint?: string;
  shouldAutoComplete?: boolean;
  defaultText?: string;
  onBlur?: () => void;
  onWheel?: (e: $FIXME) => void;
  errorText?: string;
  endAdornment?: JSX.Element;
  type?: 'number' | 'text';
  spellcheck?: boolean;
  oneClickUrlForReviewer?: string;
  useSaplingGrammarCheck?: boolean;
  showWordCounter?: boolean;
  flexibleMinRows?: boolean;
  disablePasting?: boolean;
  showLatexPreview?: boolean;
  showMarkdownPreview?: boolean;
  showDiff?: boolean;
  showEquationBuilder?: boolean;
  codingFields?: string[];
  logBehaviourActions?: LogBehaviourActions;
  chatTaskBehaviorLogMetadata?: ChatTaskBehaviorLogMetadata;
  fileUploadProps?: FileUploadProps;
  disableCopying?: boolean;
  rubric?: string;
  enableRTLText?: boolean;
}

interface AwayFocus {
  type: 'away';
}

interface ListItemFocus {
  type: 'listItem';
  index: number;
}

type TextFieldFocus = AwayFocus | ListItemFocus;

enum KeyCode {
  Escape = 27,
  ArrowUp = 38,
  ArrowDown = 40,
}

interface TypingEvent {
  timestamp: number;
  lengthDiff: number;
}

export const AUTOTYPER_DETECT_STEP = 25;
export const AUTOTYPER_DETECT_LENGTH = 100;
export const AUTOTYPER_DETECT_MAX_INTERVAL_MS = 2000;
export const MAX_ALLOWED_PASTE_LENGTH = 10;
export const DISABLE_PASTING_ERROR_TEXT = `This field doesn't allow pasting`;
export const DISABLE_COPYING_ERROR_TEXT = `This field doesn't allow copying`;
export const DISABLE_DRAG_AND_DROP_ERROR_TEXT = `This field doesn't allow drag and drop`;

// eslint-disable-next-line complexity
const CustomTextField: React.FC<CustomTextFieldProps> = props => {
  const autoComplete = useAutoComplete();
  const [Sapling, setSapling] = useState<any>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState<string>('');
  const [typingEvents, setTypingEvents] = useState<TypingEvent[]>([]);
  const {
    emitTypingEvent: chatTaskEmitTypingEvent,
    incMouseLeaveEvent: chatTaskIncMouseLeaveEvent,
  } = useAttemptBehaviorLog();

  const [errorText, setErrorText] = useState<string | undefined>(props.errorText);
  const [value, setValue] = useState<string | undefined>(props.value);

  useEffect(() => {
    if (props.defaultText && !props.value) {
      setValue(props.defaultText);
      debouncedSetIndexValue(props.index, props.setIndexValue, props.defaultText);
    } else if (props.value !== value) {
      setValue(props.value);
    }
  }, [props.defaultText, props.value]);

  useEffect(() => {
    setErrorText(props.errorText);
  }, [props.errorText]);

  const searchResults: SearchResult[] = useMemo(() => {
    if (!props.shouldAutoComplete || search.length <= 3) {
      return [];
    }
    return autoComplete.search(search).slice(0, 5);
  }, [autoComplete, search, props.shouldAutoComplete]);
  const textFieldName = `${props.title}-${props.index ?? 0}`;
  const [focus, setFocus] = useState<TextFieldFocus>({ type: 'away' });
  const setAwayFocus = useCallback(() => {
    if (focus.type !== 'away') {
      setFocus({ type: 'away' });
      autoComplete.blur();
    }
  }, [autoComplete, setFocus, focus]);
  // Unfortunately autoFocus on TextField only works for component mount.
  const setInputFocus = useCallback(() => {
    if (inputRef.current) {
      focusAtEnd(inputRef.current);
    }
    setAwayFocus();
  }, [setAwayFocus, inputRef]);
  const setListItemFocus = (index: number) => {
    setFocus({ type: 'listItem', index });
    autoComplete.focus(searchResults[index]);
  };

  // Dynamic Import of Sapling
  useEffect(() => {
    import('@scale/frontend-lib/sapling-module')
      .then(mod => {
        setSapling(mod.default);
        return;
      })
      .catch(_ => {
        setSapling(null);
      });
  }, []);

  useEffect(() => {
    if (!props.useSaplingGrammarCheck) {
      return;
    }
    // Input field in TextField component.
    const editor = document.querySelector(`textarea[name='${textFieldName}']`) as HTMLElement;
    if (editor && Sapling) {
      editor.setAttribute('contenteditable', 'true');
      editor.setAttribute('data-gramm', 'false');
      Sapling.observe(editor);
    }
    // eslint-disable-next-line
  }, [props.useSaplingGrammarCheck, Sapling]);

  const onCopy = useCallback(
    (event: any) => {
      if (props.disableCopying) {
        setErrorText(DISABLE_COPYING_ERROR_TEXT);
        event.preventDefault();
      }
    },
    [props.disableCopying],
  );

  // eslint-disable-next-line
  const debouncedSetIndexValue = useCallback(
    _.debounce((index, setIndexValue, v) => {
      setIndexValue(index, v);
    }, 500),
    [],
  );

  // eslint-disable-next-line
  const debouncedDetectAutotyper = useCallback(
    _.debounce((newTypingEvents: TypingEvent[]) => {
      if (!newTypingEvents || newTypingEvents.length < AUTOTYPER_DETECT_LENGTH) {
        return;
      }
      const keystrokeIntervals: number[] = [];
      for (const i of [...Array(newTypingEvents.length - 1).keys()]) {
        keystrokeIntervals.push(newTypingEvents[i + 1].timestamp - newTypingEvents[i].timestamp);
      }
      const numKeystrokes = keystrokeIntervals.length;
      let autotyperDetected = false;
      for (let step = 0; step < numKeystrokes; step = step + AUTOTYPER_DETECT_STEP) {
        if (step + AUTOTYPER_DETECT_LENGTH > numKeystrokes) {
          break;
        }
        const subset = keystrokeIntervals.slice(step, step + AUTOTYPER_DETECT_LENGTH);

        const avgInterval = _.mean(subset) ?? 0.0;
        if (avgInterval > AUTOTYPER_DETECT_MAX_INTERVAL_MS) {
          continue;
        }
        if (avgInterval / 2 > (_.max(subset) ?? 0.0) - (_.min(subset) ?? 0.0)) {
          autotyperDetected = true;
          break;
        }
      }
      if (props.disablePasting && autotyperDetected) {
        props.setIndexValue(0, '');
        setErrorText(DISABLE_PASTING_ERROR_TEXT);
        if (props.isTestQuestion) {
          analytics.track('Training - User tried to paste');
        }
      }
      if (
        autotyperDetected &&
        props.fieldId &&
        props.logBehaviourActions?.handleAutotyperDetectedEvent
      ) {
        props.logBehaviourActions?.handleAutotyperDetectedEvent(props.fieldId);
      }
    }, 1000),
    [props.fieldId, props.logBehaviourActions],
  );

  const onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const lengthDiff = event.target.value.length - (value?.length ?? 0);
    if (props.disablePasting) {
      // Don't block undo events.
      const undoEvent = _.get(event, 'nativeEvent.inputType')?.toLowerCase().indexOf('undo') !== -1;
      // Extra check against pasting since event.preventDefault can be suppressed by some browser extensions
      if (!undoEvent && lengthDiff > MAX_ALLOWED_PASTE_LENGTH) {
        setErrorText(DISABLE_PASTING_ERROR_TEXT);
        if (props.isTestQuestion) {
          analytics.track('Training - User tried to paste');
        }
        return;
      }
      setErrorText(undefined);
    }
    setValue(event.target.value);
    debouncedSetIndexValue(props.index, props.setIndexValue, event.target.value);
    setSearch(event.target.value);
    setAwayFocus();

    // Saving typing events to aggregate and log them later.
    const timestamp = event.nativeEvent.timeStamp;
    const newTypingEvents = [
      ...typingEvents,
      {
        timestamp,
        lengthDiff,
      },
    ];
    setTypingEvents(newTypingEvents);
    debouncedUpdateBehaviourLog(newTypingEvents);
    debouncedDetectAutotyper(newTypingEvents);
    if (props.fieldId && chatTaskEmitTypingEvent) {
      chatTaskEmitTypingEvent({
        fieldId: props.fieldId,
        text: event.target.value,
        responseIndex: props.chatTaskBehaviorLogMetadata?.responseIndex,
        suffix: props.chatTaskBehaviorLogMetadata?.suffix,
      });
    }
  };

  // eslint-disable-next-line
  const debouncedUpdateBehaviourLog = useCallback(
    _.debounce((newTypingEvents: TypingEvent[]) => {
      if (!props.fieldId || !newTypingEvents || !newTypingEvents.length) {
        return;
      }

      if (props.logBehaviourActions?.handleActiveTypingEvent) {
        props.logBehaviourActions?.handleActiveTypingEvent(
          props.fieldId,
          (_.last(newTypingEvents)?.timestamp ?? 0) - (_.first(newTypingEvents)?.timestamp ?? 0),
        );
      }

      if (props.logBehaviourActions?.appendLengthDiffEvents) {
        props.logBehaviourActions?.appendLengthDiffEvents(
          props.fieldId,
          newTypingEvents.map((e: TypingEvent) => e.lengthDiff),
        );
      }

      setTypingEvents([]);
    }, 800),
    [props.fieldId, props.logBehaviourActions],
  );

  const onSelect = (searchResult: SearchResult): void => {
    setInputFocus();
    const newValue = props.value.slice(0, searchResult.start) + searchResult.text;
    props.setIndexValue(props.index, newValue);
    setSearch('');
  };

  const onKeyDown = (event: React.KeyboardEvent<any>): void => {
    if (searchResults.length === 0) {
      return;
    }
    if (event.keyCode === KeyCode.ArrowDown) {
      let nextIndex = 0;
      if (focus.type === 'listItem') {
        nextIndex = focus.index + 1;
      }
      if (nextIndex >= searchResults.length) {
        setInputFocus();
      } else {
        setListItemFocus(nextIndex);
      }
      event.preventDefault();
      return;
    }
    if (event.keyCode === KeyCode.ArrowUp) {
      if (focus.type === 'listItem') {
        const nextIndex = focus.index - 1;
        if (nextIndex < 0) {
          setInputFocus();
        } else {
          setListItemFocus(nextIndex);
        }
        event.preventDefault();
        return;
      }
    }
    if (event.keyCode === KeyCode.Escape) {
      setSearch('');
      setInputFocus();
      return;
    }
  };

  const onMouseEnter = (searchResult: SearchResult) => {
    autoComplete.focus(searchResult);
  };

  const onMouseLeave = () => {
    autoComplete.blur();
    if (props.fieldId && props.logBehaviourActions?.handleMouseLeaveEvent) {
      props.logBehaviourActions.handleMouseLeaveEvent(props.fieldId);
    }
    if (props.fieldId && chatTaskIncMouseLeaveEvent) {
      chatTaskIncMouseLeaveEvent({
        fieldId: props.fieldId,
        responseIndex: props.chatTaskBehaviorLogMetadata?.responseIndex,
        suffix: props.chatTaskBehaviorLogMetadata?.suffix,
      });
    }
  };

  const isURL = (val: string) => validator.isURL(val);
  const helperText = getHelperText({
    // If the TextField represents a URL, pass the value to populate a 1-click link as part of the helper text during review.
    oneClickUrlForReviewer: isURL(props.value) && props.disabled ? props.value : undefined,
    maxChars: props.maxChars,
    minChars: props.minChars,
    minTokens: props.minTokens,
    maxTokens: props.maxTokens,
    chars: props.value.length,
    tokens: props.value.length ? _.words(props.value).length : 0,
    showWordCounter: props.showWordCounter,
  });
  const listItemFocusIndex = focus.type === 'listItem' ? focus.index : -1;
  const onClickAway = useCallback(() => {
    setSearch('');
    setAwayFocus();
  }, [setAwayFocus, setSearch]);
  const classes = useStyles();

  const markdownValue =
    props.codingFields && props.codingFields.length > 0
      ? addCodeFieldsToMarkdown(props.value, props.codingFields)
      : props.value;

  const [latex, setLatex] = useState<string>('');
  const [asciiMath, setAsciiMath] = useState<string>('');

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <div className={props.classes?.root}>
        <div className={classes.container}>
          <FieldHeader
            title={props.title || ''}
            description={props.description}
            hint={props.hint}
            rubric={props.rubric}
          />
          {props.fileUploadProps && <MultipartFileUpload {...props.fileUploadProps} />}
          {props.type === 'number' ? (
            <TextField
              data-field-id={props.fieldId}
              onPaste={event => {
                if (props.isTestQuestion) {
                  analytics.track('Training - User tried to paste');
                }
                if (props.disablePasting) {
                  setErrorText(DISABLE_PASTING_ERROR_TEXT);
                  event.preventDefault();
                }
              }}
              onCopy={onCopy}
              onDragOver={event => {
                if (props.disablePasting) {
                  event.preventDefault();
                }
              }}
              onDrop={event => {
                if (props.disablePasting) {
                  setErrorText(DISABLE_DRAG_AND_DROP_ERROR_TEXT);
                  event.preventDefault();
                }
              }}
              name={textFieldName}
              type={props.type || 'text'}
              disabled={props.disabled}
              onChange={onChange}
              onKeyDown={onKeyDown}
              onMouseLeave={onMouseLeave}
              value={value}
              autoFocus={props.autoFocus}
              error={!!props.errorText}
              onBlur={() => {
                props.onBlur && props.onBlur();
              }}
              onWheel={(e: $FIXME) => {
                props.onWheel && props.onWheel(e);
              }}
            />
          ) : (
            <UtilitiesWrapper disableFlex enableRTL={props.enableRTLText}>
              <TextArea
                data-field-id={props.fieldId}
                onPaste={event => {
                  if (props.isTestQuestion) {
                    analytics.track('Training - User tried to paste');
                  }
                  if (props.disablePasting) {
                    setErrorText(DISABLE_PASTING_ERROR_TEXT);
                    event.preventDefault();
                  }
                }}
                onCopy={onCopy}
                onDragOver={event => {
                  if (props.disablePasting) {
                    event.preventDefault();
                  }
                }}
                onDrop={event => {
                  if (props.disablePasting) {
                    setErrorText(DISABLE_DRAG_AND_DROP_ERROR_TEXT);
                    event.preventDefault();
                  }
                }}
                name={textFieldName}
                disabled={props.disabled}
                onChange={onChange}
                onKeyDown={onKeyDown}
                onMouseLeave={onMouseLeave}
                value={value}
                autoFocus={props.autoFocus}
                error={!!props.errorText}
                onBlur={() => {
                  props.onBlur && props.onBlur();
                }}
                onWheel={(e: $FIXME) => {
                  props.onWheel && props.onWheel(e);
                }}
                data-testid="editor-content"
              />
            </UtilitiesWrapper>
          )}
          {errorText || helperText ? (
            <HelperText error={!!errorText} className="px-1 w-full">
              {errorText || helperText}
            </HelperText>
          ) : null}
          {/* if showDiff, render in diff column */}
          {props.showLatexPreview && !props.showDiff && (
            <div className="mt-2">
              <Typography.Paragraph3>LaTex Preview:</Typography.Paragraph3>
              <ChatMarkdown
                enableMath={true}
                enableSingleDollarTextMath={true}
                markdown={replaceWithDoubleDollarSign(props.value)}
              />
            </div>
          )}
          {props.showMarkdownPreview && !props.showDiff && (
            <div className={twMerge(classes.markdown, 'mt-2')}>
              <Typography.Paragraph3>Markdown Preview:</Typography.Paragraph3>
              {markdownValue && <ChatMarkdown markdown={markdownValue} />}
            </div>
          )}
          {props.showEquationBuilder && (
            <div className={twMerge(classes.equationContainer, 'mt-2')}>
              <div className="flex flex-col gap-2">
                <Typography.Paragraph3>Equation Builder:</Typography.Paragraph3>
                <MathEditor
                  value={latex}
                  className={classes.equationTextbox}
                  onChange={(latex: string, asciiMath: string) => {
                    setLatex(latex);
                    setAsciiMath(asciiMath);
                  }}
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setValue(value + `$${latex}$`);
                      props.setIndexValue(props.index, value + `$${latex}$`);
                      setLatex('');
                      setAsciiMath('');
                    }}
                    disabled={latex === ''}
                    className={classes.button}
                  >
                    Insert Latex
                  </Button>
                  <Button
                    onClick={() => {
                      setValue(value + `\`\`${asciiMath}\`\``);
                      props.setIndexValue(props.index, value + `\`\`${asciiMath}\`\``);
                      setLatex('');
                      setAsciiMath('');
                    }}
                    disabled={asciiMath === ''}
                    className={classes.button}
                  >
                    Insert AsciiMath
                  </Button>
                </div>
              </div>
              <div className={twMerge(classes.markdown, 'mt-2')}>
                <Typography.Paragraph3>Rendered Preview:</Typography.Paragraph3>
                {props.value && (
                  <ChatMarkdown
                    enableMath={true}
                    enableSingleDollarTextMath={true}
                    markdown={props.value}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        <TextAutoComplete
          options={searchResults}
          onSelect={onSelect}
          autoFocusIndex={listItemFocusIndex}
          onKeyDown={onKeyDown}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      </div>
    </ClickAwayListener>
  );
};

export default CustomTextField;

function focusAtEnd(input: HTMLInputElement): void {
  input.focus();
  try {
    input.selectionStart = input.value.length;
    input.selectionEnd = input.value.length;
  } catch (e) {
    // legacy browser does not support input selection
  }
}

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    color: '#000000',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    marginTop: '8px',
    marginBottom: '8px',
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
  },
  description: {
    color: theme.colors.gray50,
    marginBottom: 16,
    fontSize: 12,
  },
  tooltipInfoIcon: {
    paddingLeft: 8,
    paddingRight: 8,
    color: theme.colors.gray50,
    marginTop: 6,
    minWidth: 14,
    minHeight: 14,
  },
  tooltipText: {
    maxWidth: 496,
  },
  equationContainer: {
    padding: 8,
    borderRadius: theme.spacing(1),
    borderColor: theme.colors.gray20,
    borderWidth: '1px !important',
  },
  equationTextbox: {
    width: '100%',
    fontSize: '14px',
    lineHeight: '28px',
    letterSpacing: '0.1px',
    borderRadius: theme.spacing(1),
    borderColor: theme.colors.gray20,
    borderWidth: '1px !important',
    marginBottom: 4,
  },
  button: {
    margin: '0 3px',
  },
  markdown: {
    lineHeight: 0,
    '& table, & pre, & h1, & h2, & h3, & h4, & h5': {
      lineHeight: 1,
    },
  },
}));
